import { create } from 'zustand';
import { Timestamp, type Unsubscribe } from '@firebase/firestore';
import {
  DefensePlan,
  DefensePlanFeature,
  DefensePlanAssignment,
  PlanAssignmentUpdate,
  PlanParty,
  PlanBundle,
  DefenseStrategy,
  DefenseFeatureKind,
  PlanVisibility,
  PlanAssignmentStatus,
  PlanBid,
  FundingRequest,
  User,
} from '../types';
import {
  MAX_BUNDLES_PER_PLAN,
  MAX_BUNDLE_TEAM,
  MAX_BUNDLE_INVITED_VENDORS,
} from '../schemas/firestoreSchemas';
import { defensePlansApi } from '../api/defensePlans';
import { planBidsApi, submitBid, type BidRequest, type BidResult } from '../api/planBids';
import { fundingRequestsApi, toFundingError } from '../api/fundingRequests';
import { bundleIdsKeptByFunding } from '../utils/money';
import {
  defensePlanPartiesApi,
  assignmentId as buildAssignmentId,
  type PartyInput,
} from '../api/defensePlanParties';
import {
  requestPlanUpdate as requestPlanUpdateApi,
  publishPlanUpdateNews as publishPlanUpdateNewsApi,
  sendCommunityUpdate as sendCommunityUpdateApi,
  subscribeAssignmentUpdates,
  type RequestPlanUpdateResult,
  type PublishPlanUpdateNewsResult,
  type SendCommunityUpdateResult,
} from '../api/defensePlanUpdates';
import { applyAddFeature, applyRemoveFeature, newestPlan, planBounds } from '../utils/planGeometry';
import {
  applyBundleMembership,
  moveToBundle,
  pruneEmptyBundles,
  effectiveBundleTeam,
  effectiveBundleLeader,
  withBundleTeam,
  reconcileTeamAssignments,
  stripPartyFromBundles,
  stripBundleTeams,
  stripInvitedVendors,
  withInvitedVendorToggled,
  withVendorInvitedAt,
  type InvitedVendorEntry,
  bundleIdsKeptByBids,
} from '../utils/planBundles';
import { nearestStreetAddress } from '../utils/planHomes';
import { useAuthStore } from './authStore';
import { bundleIdsKeptByEvents } from './planEventsStore';
import { useServiceAreasStore } from './serviceAreasStore';
import { useStructuresStore } from './structuresStore';
// Lazy/circular with uiStore (which imports this store back for reset() and
// signOut()) is safe here: both references are read inside callback bodies
// at call time, never at module-evaluation time, so it doesn't matter which
// of the two files finishes loading first.
import { useUIStore } from './uiStore';
import { getErrorMessage } from '../utils/errorHandler';
import { EMAIL_SHAPE } from '../utils/peopleLists';
import { markPlanFeatureAddedLocally } from '../utils/recentPlanFeatures';
import { createPrefixedLogger } from '../utils/logger';

const log = createPrefixedLogger('defensePlansStore');

// Module-level subscription handles, same pattern as postsStore's
// mapPostsUnsub/feedUnsub: a zustand store's own state isn't a safe place
// to stash an Unsubscribe closure across StrictMode double-invocation. Each
// subscription also tracks the key it was opened with (uid+areaId, areaId,
// or planId) so subscribe*() can tell "already subscribed to this" apart
// from "subscribed to something else, re-subscribe" — mirrors
// structuresStore.subscribeToAreaClaims's sameIds check.
let ownPlanUnsub: Unsubscribe | null = null;
let ownPlanKey: string | null = null;
let areaPlansUnsub: Unsubscribe | null = null;
let areaPlansKey: string | null = null;
let viewedPlanUnsub: Unsubscribe | null = null;
let viewedPlanKey: string | null = null;
// Party + assignment subscriptions, one per role the same way the plan
// subscriptions above are: the editor needs "who" for ownPlan while it's
// open, the viewer needs it for whatever plan it's showing. Both can point
// at the same plan id (the owner viewing their own plan) — that's a
// harmless duplicate listener, not a bug, kept simple rather than deduped
// across roles.
let ownPartiesUnsub: Unsubscribe | null = null;
let ownPartiesKey: string | null = null;
let viewedPartiesUnsub: Unsubscribe | null = null;
let viewedPartiesKey: string | null = null;
let ownAssignmentsUnsub: Unsubscribe | null = null;
let ownAssignmentsKey: string | null = null;
let viewedAssignmentsUnsub: Unsubscribe | null = null;
let viewedAssignmentsKey: string | null = null;
// Assignment updates (Sept 2026 update loop) — owner-only feed of reported
// progress on the currently-open plan. No "viewed" counterpart: only the
// owner ever sees this, from the docked editor panel.
let ownAssignmentUpdatesUnsub: Unsubscribe | null = null;
let ownAssignmentUpdatesKey: string | null = null;
// Vendor bids on the owner's open plan (Oct 2026). Owner only.
let ownBidsUnsub: Unsubscribe | null = null;
let ownBidsKey: string | null = null;
let ownFundingUnsub: Unsubscribe | null = null;
let ownFundingKey: string | null = null;

// Serialises every Firestore write (create, writeFeatures, updateTitle,
// setVisibility, delete) through one chain so two rapid edits (e.g. two
// vertices committed back to back) land in the order they were made rather
// than racing — the Decisions-locked-in note in the plan doc calls this out
// explicitly, because writeFeatures always sends the full features array,
// so an out-of-order write would silently drop the earlier one's feature.
// writePlanDoc (below) adds coalescing and pacing on top of this ordering for
// the full-state plan writes.
let writeChain: Promise<void> = Promise.resolve();

// Mirrors firestore.rules: the partyContacts email shape and the 80-char
// cap on a party's organization and person.
const PARTY_EMAIL_PATTERN = EMAIL_SHAPE;
const PARTY_NAME_MAX = 80;

function enqueueWrite(task: () => Promise<void>): Promise<void> {
  const run = writeChain.then(task);
  // The caller still gets the rejection from `run`; the chain itself must
  // not keep it. A rejected chain skips every later task, and since those
  // tasks are what clear isSaving, the panel sat on "Saving…" until a
  // reload (Glenn, Oct 8 2026).
  writeChain = run.catch(() => {});
  return run;
}

// Firestore sustains about one write per second per document. Every defense
// edit rewrites the whole plan doc, so a burst (delete N selected rows, a drag
// plus undo, several copies) used to send N writes and tripped
// `resource-exhausted` ("exceeded maximum bandwidth for writes") in the
// client's browser on Oct 14 2026, after which the SDK sat in maximum backoff.
// writePlanDoc fixes that for the full-state writes: each call takes a
// sequence number, and when its turn comes in the write chain it skips itself
// if a newer call for the same plan is already queued. Every caller builds its
// payload on the current ownPlan, so the newest payload contains all earlier
// edits and the burst costs one write. Consecutive real writes to one plan
// start at least PLAN_WRITE_MIN_GAP_MS apart. Different plans don't wait on
// each other. A rejection from writeFeatures reaches the caller.
const PLAN_WRITE_MIN_GAP_MS = 1100;
let planWriteSeq = 0;
const latestPlanWriteSeq = new Map<string, number>();
const lastPlanWriteStartedAt = new Map<string, number>();

function writePlanDoc(
  planId: string,
  payload: Parameters<typeof defensePlansApi.writeFeatures>[1]
): Promise<'written' | 'superseded'> {
  const seq = ++planWriteSeq;
  latestPlanWriteSeq.set(planId, seq);
  let result: 'written' | 'superseded' = 'superseded';
  return enqueueWrite(async () => {
    if (latestPlanWriteSeq.get(planId) !== seq) return;
    const wait =
      (lastPlanWriteStartedAt.get(planId) ?? -Infinity) + PLAN_WRITE_MIN_GAP_MS - Date.now();
    if (wait > 0) await new Promise<void>((resolve) => setTimeout(resolve, wait));
    lastPlanWriteStartedAt.set(planId, Date.now());
    await defensePlansApi.writeFeatures(planId, payload);
    result = 'written';
  }).then(() => result);
}

function newFeatureId(): string {
  // crypto.randomUUID exists in every runtime this app ships to (modern
  // browsers, Hermes with the polyfill), but fall back rather than crash a
  // draw commit if it's ever missing.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `feature-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function newBundleId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `bundle-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// Remembers which of a user's several plans was last active, across
// reloads. Same feature-detect + try/catch shape as the outbox debug flag
// (src/api/outbox/syncEngine.ts) rather than a Platform.OS check — the
// editor is web-only in practice, but this reads and writes fine wherever
// localStorage happens to exist (or quietly does nothing where it doesn't:
// a locked-down context, private mode, or the RN test environment).
function activePlanStorageKey(uid: string): string {
  return `redi.activeDefensePlanId.${uid}`;
}

function readPersistedActivePlanId(uid: string): string | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage.getItem(activePlanStorageKey(uid));
  } catch {
    return null;
  }
}

function writePersistedActivePlanId(uid: string, planId: string | null): void {
  try {
    if (typeof localStorage === 'undefined') return;
    if (planId) localStorage.setItem(activePlanStorageKey(uid), planId);
    else localStorage.removeItem(activePlanStorageKey(uid));
  } catch {
    // ignore — see readPersistedActivePlanId
  }
}

// Priority: an explicit choice still present in the list, else the id
// persisted from a previous session (also still present), else the most
// recently updated plan. Pure so the resolution logic is testable without a
// live subscription — see __tests__/store/defensePlansStore.test.ts.
function resolveActiveOwnPlanId(
  plans: DefensePlan[],
  requestedId: string | null,
  persistedId: string | null
): string | null {
  if (requestedId && plans.some((p) => p.id === requestedId)) return requestedId;
  if (persistedId && plans.some((p) => p.id === persistedId)) return persistedId;
  return newestPlan(plans)?.id ?? null;
}

// Swaps one plan into a list by id, used to keep `ownPlans` and the derived
// `ownPlan` consistent after every optimistic write and its resolution
// (success or rollback) — mirrors syncViewed's "match by id" shape below.
function replacePlanInList(plans: DefensePlan[], updated: DefensePlan): DefensePlan[] {
  return plans.map((p) => (p.id === updated.id ? updated : p));
}

// The current user's display name, same composition UserMenuDropdown uses
// (`${firstName} ${lastName}`.trim()), with a fallback for the edge case of
// an account with no name on file. Title falls back to "My plan" rather
// than "User's plan" per the plan doc.
// A second plan started from "New plan" would otherwise share the first
// one's default title, and the switcher would list two identical rows.
export function uniquePlanTitle(base: string, taken: string[]): string {
  if (!taken.includes(base)) return base;
  let n = 2;
  while (taken.includes(`${base} ${n}`)) n += 1;
  return `${base} ${n}`.slice(0, 80);
}

function ownerIdentityFor(user: User): { ownerName: string; title: string } {
  const rawName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
  return {
    ownerName: rawName || 'User',
    title: rawName ? `${rawName}'s plan` : 'My plan',
  };
}

// The FIRST plan keeps the identity-based default (ownerIdentityFor's
// "<name>'s plan" / "My plan"). Later ones are named for where they start:
// "Plan near 977 Club View Drive", from the home nearest the first defense
// drawn. Sept 17 2026 they were numbered ("Plan 2") so they wouldn't read as
// part of the first plan ("Glenn Owner's plan 2"); Sept 25 2026 Jason chose
// place names over numbers, since "Plan 2" says nothing about which plan it
// is. `existingCount` is the owner's plan count BEFORE this one. With no
// place known (nothing drawn yet, or no home nearby) it reads "New plan".
export function defaultNewPlanTitle(
  existingCount: number,
  user: User,
  place?: string | null
): string {
  if (existingCount === 0) return ownerIdentityFor(user).title;
  return place ? `Plan near ${place}`.slice(0, 80) : 'New plan';
}

// Where a new plan starts, for its default title: the home a home feature
// is pinned to, else the loaded home nearest the feature's middle.
function placeForNewPlan(input: AddFeatureInput): string | null {
  const pinned = input.address?.split(',')[0]?.trim();
  if (input.structureId && pinned) return pinned;
  const lat = input.coords.reduce((sum, c) => sum + c.lat, 0) / input.coords.length;
  const lng = input.coords.reduce((sum, c) => sum + c.lng, 0) / input.coords.length;
  const structures = useStructuresStore.getState();
  const loaded = Object.values(structures._structuresByShard).flat();
  loaded.push(...structures.userAddedStructures);
  return nearestStreetAddress({ lat, lng }, loaded);
}

// Names are public on a shared plan (Sept 2026 decision) and no email is
// collected in this phase, so a volunteer with no name on file still needs
// something to show on the row.
function volunteerNameFor(user: User): string {
  const rawName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
  return rawName || 'A neighbor';
}

// Selector for PlanUpdatesSheet/DefensePlanPanel's "New updates (n)"
// affordance: an update counts as unpublished until publishPlanUpdateNews
// stamps it with the post it landed in. A plain function rather than a
// store method — it only reads the map already in state.assignmentUpdatesByPlan,
// nothing to subscribe or write.
export function unpublishedAssignmentUpdates(
  updates: Record<string, PlanAssignmentUpdate>
): PlanAssignmentUpdate[] {
  return Object.values(updates).filter((u) => !u.newsPostId);
}

interface AddFeatureInput {
  strategy: DefenseStrategy;
  kind: DefenseFeatureKind;
  coords: { lat: number; lng: number }[];
  // Set only by undo (DefensePlanPanel's handleUndo): re-adds a just-deleted
  // feature under its OWN id rather than minting a new one, so an
  // owner-sourced assignment can be restored onto it afterward. Omitted on
  // every normal draw commit, which always mints a fresh id.
  id?: string;
  // Set only by home-mode draws (useDefensePlanDraw.web.ts's commitHome):
  // the structure this feature is pinned to, and its address captured at
  // draw time. Both omitted for every other kind of commit — see
  // DefensePlanFeatureSchema's comment on what makes a feature a "home"
  // feature.
  structureId?: string;
  address?: string;
  // Set only by undo (DefensePlanPanel's handleUndo), and only when the
  // deleted feature's bundle still exists — a feature re-added under its own
  // id can rejoin its old bundle, but never mints a reference to a bundle
  // that's gone.
  bundleId?: string;
}

// A plan doc with a permission-denied read is treated as "not available"
// rather than surfaced as a raw Firestore error — the usual cause is the
// doc being gone (the read rule dereferences resource.data, and a deleted
// doc has none) or the owner having flipped it to private.
const PLAN_UNAVAILABLE_NOTICE = "That plan isn't available anymore.";

/** What moveFeaturesToBundle changed, kept so restoreBundleMembership can put
 *  it back in one write. `previous` lists the `bundleId` each moved feature
 *  had (absent when it was loose); `bundles` is the whole bundles array from
 *  before the move, teams and leader included, so a bundle the move pruned
 *  returns intact. */
export interface MembershipSnapshot {
  planId: string;
  previous: { featureId: string; bundleId?: string }[];
  bundles: PlanBundle[];
}

interface DefensePlansState {
  // The full list of plans this signed-in user owns (several are allowed —
  // "Fire plan 2026", "Supplemental plan", etc.), newest-updated first (the
  // order subscribeOwnPlans delivers). `ownPlan` below is derived from this
  // list plus `activeOwnPlanId`: every place that reads `s.ownPlan` keeps
  // working unchanged, it now just means "the active one of several".
  ownPlans: DefensePlan[];
  // Explicit or resolved (see resolveActiveOwnPlanId) id of the plan being
  // edited. Null while `draftingNewPlan` is true, or when the user has no
  // plans yet.
  activeOwnPlanId: string | null;
  // Set by startNewPlan(): the panel shows the "draw your first defense"
  // blank state (ownPlan null) even though the user may already have other
  // plans, until the next addFeature mints a new doc and clears this.
  draftingNewPlan: boolean;
  // A title typed before the plan has a doc (there is no doc until the first
  // defense is drawn, so updateTitle has nothing to write to). addFeature's
  // create branch uses it instead of the default title, then clears it.
  // Dropped when the user switches plans or starts another new one.
  pendingTitle: string | null;
  // False from subscribeOwnPlan until the first snapshot (or the first
  // error) arrives, so the editor can tell "not loaded yet" from "loaded and
  // empty" and show a skeleton instead of a false empty state. Stays false
  // when nothing subscribes (signed out, anonymous); the panel checks who is
  // signed in before it waits on this.
  ownPlansLoaded: boolean;
  ownPlan: DefensePlan | null;
  areaPlans: DefensePlan[];
  viewedPlan: DefensePlan | null;
  isSaving: boolean;
  // Set only by a write (create/update/delete/title/visibility) and read
  // only by the editor's meta line — a background listener failure never
  // touches this. See subscribeOwnPlan/subscribeAreaPlans/subscribeViewedPlan
  // below, which log and stop there.
  saveError: string | null;
  // A short-lived message for the viewer panel/card to show when the plan
  // it was pointed at stops being available mid-view (deleted, or made
  // private by someone else). Cleared by clearViewedPlanNotice, which the
  // viewer calls after showing it for a few seconds.
  viewedPlanNotice: string | null;

  // "Who's responsible" per plan: every party record, keyed by planId then
  // partyId, and every featureId+partyId link, keyed by planId then
  // assignment id (buildAssignmentId). No link for a feature means "Needs
  // someone" — no placeholder doc is ever written for that state. Populated
  // by whichever of the subscriptions below is open; unsubscribing does not
  // clear a plan's entry (the next subscribe replaces it from the first
  // snapshot, and stale-while-closed rows are harmless since nothing reads
  // them).
  partiesByPlan: Record<string, Record<string, PlanParty>>;
  assignmentsByPlan: Record<string, Record<string, DefensePlanAssignment>>;
  // Optimistically hidden ahead of a deferred deleteOwnerParty commit
  // (panel-system.md Phase 1's undo toast for "Remove party" —
  // src/utils/deferredCommit.ts): the party disappears from the roster, the
  // assign picker and feature rows the moment the owner presses remove, six
  // seconds before the real write goes out, so Undo can restore it without a
  // round trip. Keyed by partyId alone (ids are unique across plans) rather
  // than nested under planId — nothing here needs to scope by plan.
  hiddenPartyIds: Record<string, true>;
  hideParty: (partyId: string) => void;
  unhideParty: (partyId: string) => void;
  // Reported progress on the owner's currently-open plan (Sept 2026 update
  // loop), keyed by planId then update id. Owner-only — populated only by
  // subscribeOwnPlanAssignmentUpdates, while the editor panel is open.
  assignmentUpdatesByPlan: Record<string, Record<string, PlanAssignmentUpdate>>;
  // Vendor bids on the owner's open plan (Oct 2026), oldest first. Owner
  // only: populated by subscribeBids while the editor is open. Rules deny
  // anyone else, so a viewer never has an entry.
  bidsByPlan: Record<string, PlanBid[]>;
  // The owner's funding requests on their open plan (Oct 2026, Redi bucks).
  // Same life as bidsByPlan. Rules let a request's owner read it and nobody
  // but moderators read anyone else's.
  fundingRequestsByPlan: Record<string, FundingRequest[]>;

  // Plan ids that have an optimistic entry in `ownPlans`/`ownPlan` but whose
  // createPlan() write hasn't resolved yet — set by addFeature (first
  // feature) and duplicateActivePlan right when they optimistically add the
  // plan, cleared once the write settles (success or failure). The plan doc
  // doesn't exist server-side until that write lands, so
  // useDefensePlanLayer.web.ts reads this to hold off opening the parties/
  // assignments subscriptions until then — rules deny reads under a missing
  // parent, and `ownPlan?.id` alone doesn't change across the write (same
  // id optimistic and confirmed), so it can't be the gate on its own.
  unpersistedPlanIds: Record<string, true>;

  subscribeOwnPlan: (uid: string) => void;
  unsubscribeOwnPlan: () => void;
  subscribeAreaPlans: (areaId: string) => void;
  unsubscribeAreaPlans: () => void;
  subscribeViewedPlan: (planId: string) => void;
  unsubscribeViewedPlan: () => void;
  subscribeOwnPlanParties: (planId: string) => void;
  unsubscribeOwnPlanParties: () => void;
  subscribeViewedPlanParties: (planId: string) => void;
  unsubscribeViewedPlanParties: () => void;
  subscribeOwnPlanAssignments: (planId: string) => void;
  unsubscribeOwnPlanAssignments: () => void;
  subscribeViewedPlanAssignments: (planId: string) => void;
  unsubscribeViewedPlanAssignments: () => void;
  subscribeOwnPlanAssignmentUpdates: (planId: string) => void;
  unsubscribeOwnPlanAssignmentUpdates: () => void;
  subscribeBids: (planId: string) => void;
  unsubscribeBids: () => void;
  subscribeFundingRequests: (planId: string, uid: string) => void;
  unsubscribeFundingRequests: () => void;

  addFeature: (input: AddFeatureInput) => Promise<void>;
  /** "Copy as another defense": adds a feature with the same shape, place
   *  and bundle as `featureId` under a different strategy. No note and a
   *  fresh createdAt. Returns the new id as soon as the copy is in the
   *  plan, or null when the source is gone or the add was refused (the
   *  200-feature cap lands in saveError). The write settles in the
   *  background, like a draw. */
  copyFeatureAs: (featureId: string, strategy: DefenseStrategy) => string | null;
  removeFeature: (featureId: string) => Promise<void>;
  updateTitle: (title: string) => Promise<void>;
  /** Resolves true once the write lands, false if it failed (the change is
   *  rolled back). Callers confirm to the user only on true. */
  setVisibility: (visibility: PlanVisibility) => Promise<boolean>;
  // Defaults to the active plan when planId is omitted. Active then falls
  // back to the newest remaining plan (or null when none are left).
  deleteOwnPlan: (planId?: string) => Promise<void>;
  // Switches the active plan to one already in `ownPlans`; a no-op if
  // planId isn't one of this owner's plans.
  setActiveOwnPlan: (planId: string) => void;
  // Clears the active plan and sets draftingNewPlan, so the panel shows the
  // blank "draw your first defense" state without discarding any existing
  // plan — the next addFeature mints a new doc and makes it active.
  startNewPlan: () => void;
  // Creates a new plan from the active one: same features (ids and all
  // fields kept) and visibility, title "Copy of <title>" (truncated to 80
  // chars). Parties/assignments are never copied. Makes the copy active.
  duplicateActivePlan: () => Promise<void>;

  // --- Bundles (Sept 16 2026 — "bundle steps") ----------------------------
  // All four act on the active plan (ownPlan), the same shape as
  // updateTitle/setVisibility above: no planId argument, a no-op when there
  // is none. See src/utils/planBundles.ts for the pure membership math.
  /** Groups `featureIds` under a new bundle named `name`, moving any of them
   *  out of a bundle they were already in (a feature belongs to at most
   *  one). A source bundle left with no members is dropped. No-op with an
   *  empty name or an empty selection. Resolves to the new bundle's id once
   *  the write lands, or null when nothing was created or the write failed. */
  createBundle: (name: string, featureIds: string[]) => Promise<string | null>;
  /** Moves `featureIds` into an existing bundle (adding to its members,
   *  never replacing them), or out of their bundle when `bundleId` is null.
   *  A bundle left with no members is dropped. One plan write. Resolves to a
   *  snapshot for restoreBundleMembership, or null when nothing changed
   *  (no plan, unknown bundle, no matching features, already there) or the
   *  write failed. */
  moveFeaturesToBundle: (
    featureIds: string[],
    bundleId: string | null
  ) => Promise<MembershipSnapshot | null>;
  /** Undo for moveFeaturesToBundle: puts each moved feature back in its
   *  previous bundle (or out of any) and brings back the snapshot's bundles,
   *  with their teams, keeping any bundle created since that still has
   *  members. One plan write. No-op when the snapshot is for another plan,
   *  or when the result would exceed the bundle cap (saveError is set). */
  restoreBundleMembership: (snapshot: MembershipSnapshot) => Promise<void>;
  renameBundle: (bundleId: string, name: string) => Promise<void>;
  /** Undo for ungroupBundle: recreates the bundle with its old id and name
   *  and moves `featureIds` back in. Members that no longer exist are
   *  skipped; no-op if the id is already taken or nothing is left to group. */
  restoreBundle: (bundle: PlanBundle, featureIds: string[]) => Promise<void>;
  /** Disbands a bundle: every current member's `bundleId` is cleared, the
   *  bundle itself is removed. The features stay in the plan. */
  ungroupBundle: (bundleId: string) => Promise<void>;
  /** Replaces a bundle's membership wholesale (used by the panel's edit-
   *  selection flow). An empty `featureIds` disbands it, same as
   *  ungroupBundle. */
  setBundleMembers: (bundleId: string, featureIds: string[]) => Promise<void>;
  /** Adds `partyId` to a bundle's team (and optionally makes them leader),
   *  then links them to every current member defense. The bundle owns the
   *  team: the first team edit persists the derived team (see
   *  effectiveBundleTeam) so it starts from what the owner saw. A bundle has
   *  no assignment record of its own, only its members do. */
  addBundleTeammate: (
    bundleId: string,
    partyId: string,
    data: { description?: string; status: PlanAssignmentStatus; leader?: boolean }
  ) => Promise<void>;
  /** Drops `partyId` from the team, clears the leader if it was them, and
   *  deletes their links on the bundle's member defenses. */
  removeBundleTeammate: (bundleId: string, partyId: string) => Promise<void>;
  /** Names the leader (a teammate) or clears it with null. One plan-doc
   *  write; assignments are untouched since the leader is a label only. */
  setBundleLeader: (bundleId: string, partyId: string | null) => Promise<void>;
  /** Sets what one teammate does, on the team entry and on every member
   *  defense they are linked to, leaving each link's status as it is. */
  setBundleTeammateDescription: (
    bundleId: string,
    partyId: string,
    description: string | undefined
  ) => Promise<void>;
  /** Sets one teammate's status on every member defense they are linked to. */
  setBundleTeammateStatus: (
    bundleId: string,
    partyId: string,
    status: PlanAssignmentStatus
  ) => Promise<void>;
  /** Opens or closes a bundle to vendor bids (one plan-doc write). Opening
   *  stamps bidsOpenedAt, so the Program panel can tell how long it waited.
   *  Resolves true once the write lands. */
  setBundleBidsOpen: (planId: string, bundleId: string, open: boolean) => Promise<boolean>;
  /** Shortlists a directory vendor on a bundle, or takes it off when the
   *  vendorId is already there. Capped at MAX_BUNDLE_INVITED_VENDORS. One
   *  plan-doc write; the list key stays absent until the first add. */
  toggleInvitedVendor: (bundleId: string, entry: InvitedVendorEntry) => Promise<void>;
  /** Stamps invitedAt (now) on one shortlisted vendor, when the owner opens
   *  an email invite. No-op if the vendor isn't on the bundle. */
  markVendorInvited: (bundleId: string, vendorId: string) => Promise<void>;
  /** Accepts a vendor's bid: creates a vendor party with the bid's names and
   *  email, puts it on the bundle team, then marks the bid accepted. Stops at
   *  the first step that fails, so a bid is never accepted without its
   *  vendor on the team. Other bids stay open until declined. */
  acceptBid: (planId: string, bidId: string) => Promise<boolean>;
  /** Declines a vendor's bid. Nothing else changes. */
  declineBid: (planId: string, bidId: string) => Promise<boolean>;
  /**
   * A vendor's public bid (no account), from the opportunity page. Throws a
   * BidError ({ kind, message }) on failure. Touches no store state: the
   * public page keeps its own form state.
   */
  submitBid: (request: BidRequest) => Promise<BidResult>;
  /** Redi bucks. Resolve to an error message for the view, or null. */
  requestFunding: (
    planId: string,
    bundleId: string,
    bidId: string,
    amount: number
  ) => Promise<string | null>;
  withdrawFundingRequest: (requestId: string) => Promise<string | null>;
  /** The owner's own cost guess in whole dollars. Null clears it. */
  setBundleEstimate: (
    planId: string,
    bundleId: string,
    estimate: number | null
  ) => Promise<boolean>;
  /** Marks the bundle's work done or reopens it. Done is what lets a
   *  moderator release payment. */
  setBundleWorkDone: (planId: string, bundleId: string, done: boolean) => Promise<boolean>;

  // Owner: create a brand-new party (the assign sheet's "add new party").
  // Returns the new party's id on success, null on failure (saveError is
  // also set) — the caller uses the id to immediately link it to a
  // feature via setOwnerAssignment.
  createOwnerParty: (
    planId: string,
    party: PartyInput,
    email: string | undefined
  ) => Promise<string | null>;
  // Owner: edit a party's own fields (the "People and organizations"
  // section's edit sheet).
  updateOwnerParty: (
    planId: string,
    partyId: string,
    party: PartyInput,
    email: string | undefined
  ) => Promise<void>;
  // Owner: store (or clear, with undefined) the address a party gets plan
  // updates at. A Cloud Function emails every address in a plan's
  // partyContacts, so the assign form calls this only on an explicit tap or
  // confirm, never as a side effect of assigning. Returns true when saved.
  setPartyContactEmail: (
    planId: string,
    partyId: string,
    email: string | undefined
  ) => Promise<boolean>;
  // Owner: delete a party outright — cascades its assignments and contact.
  deleteOwnerParty: (planId: string, partyId: string) => Promise<void>;
  // One-shot owner-only read (the edit sheet's initial value) — contacts
  // are never subscribed like parties/assignments are, since nothing else
  // in the UI ever shows an email.
  getPartyContactEmail: (planId: string, partyId: string) => Promise<string | undefined>;

  // Owner: create-or-update the link between a feature and a party (the
  // assign sheet's per-party status/description, for both a newly added
  // party and an existing one picked from the list).
  setOwnerAssignment: (
    planId: string,
    featureId: string,
    partyId: string,
    data: { description?: string; status: PlanAssignmentStatus }
  ) => Promise<void>;
  // Owner unlink (assign sheet's remove-party control), or assignee
  // step-back — rules decide which is allowed; the caller already knows
  // which one it is.
  removeAssignment: (planId: string, assignmentId: string) => Promise<void>;
  // A signed-in homeowner claiming a defense for themselves — reuses their
  // existing party on this plan if they have one, else creates it.
  volunteerForFeature: (planId: string, featureId: string) => Promise<void>;
  // Assignee-only status change ("Mark done").
  updateAssignmentStatus: (
    planId: string,
    assignmentId: string,
    status: PlanAssignmentStatus
  ) => Promise<void>;

  // --- Update loop (Sept 2026) — thin pass-throughs to the Cloud Functions
  // in src/api/defensePlanUpdates.ts. No optimistic state: each is a single
  // request/response action a sheet drives its own saving/error state
  // around (DefensePlanPartyEditSheet's "Ask for an update",
  // PlanUpdatesSheet's "Post to News" / "Email this to the community"),
  // same shape as getPartyContactEmail below. Errors propagate to the
  // caller rather than being swallowed here, so a sheet can read a thrown
  // FirebaseError's code/message (e.g. the ask cooldown's
  // failed-precondition).
  requestPlanUpdate: (planId: string, partyId: string) => Promise<RequestPlanUpdateResult>;
  publishPlanUpdateNews: (
    planId: string,
    updateIds: string[],
    title?: string
  ) => Promise<PublishPlanUpdateNewsResult>;
  sendCommunityUpdate: (
    planId: string,
    postId: string,
    includeMembers?: boolean
  ) => Promise<SendCommunityUpdateResult>;

  clearSaveError: () => void;
  clearViewedPlanNotice: () => void;

  reset: () => void;
}

// Keeps `viewedPlan` in step whenever it happens to be pointing at the same
// plan `ownPlan` is being edited on — the map renders `viewedPlan`, not
// `ownPlan`, so a plan the owner is actively drawing on must update both.
function syncViewed(
  viewedPlan: DefensePlan | null,
  planId: string,
  next: DefensePlan | null
): DefensePlan | null {
  return viewedPlan?.id === planId ? next : viewedPlan;
}

type SetPlanState = (
  partial: Partial<DefensePlansState> | ((state: DefensePlansState) => Partial<DefensePlansState>)
) => void;
// Shared optimistic-write-rollback shape for the four bundle actions below
// (createBundle/renameBundle/ungroupBundle/setBundleMembers): they all
// change only `features`/`bundles` on the active plan and write the whole
// doc back through writeFeatures, same as addFeature/removeFeature already
// do for the plain feature edits. `planId` is captured by the caller before
// this runs (at the same point it read `previous`/`updated` off `ownPlan`),
// so a plan switch mid-write can't misdirect the eventual write.
function writePlanUpdate(
  set: SetPlanState,
  planId: string,
  previous: DefensePlan,
  updated: DefensePlan,
  label: string
): Promise<boolean> {
  set((s) => ({
    ownPlans: replacePlanInList(s.ownPlans, updated),
    ownPlan: s.ownPlan?.id === planId ? updated : s.ownPlan,
    viewedPlan: syncViewed(s.viewedPlan, planId, updated),
    isSaving: true,
    saveError: null,
  }));

  // The write's outcome is reported back, not thrown: a failure rolls the plan
  // back and sets `saveError` here, and the bundle-team actions only touch
  // assignments after a write that succeeded (a rolled-back bundle must not
  // leave its team's links behind).
  return writePlanDoc(planId, {
    features: updated.features,
    featureCount: updated.featureCount,
    bounds: updated.bounds,
    bundles: updated.bundles,
  }).then(
    (outcome) => {
      // A superseded write leaves isSaving/saveError to the newer write.
      if (outcome === 'written') set({ isSaving: false });
      return true;
    },
    (err) => {
      log.error(`${label} failed:`, err);
      set((s) => ({
        ownPlans: replacePlanInList(s.ownPlans, previous),
        ownPlan: s.ownPlan?.id === planId ? previous : s.ownPlan,
        viewedPlan: syncViewed(s.viewedPlan, planId, previous),
        isSaving: false,
        saveError: getErrorMessage(err),
      }));
      return false;
    }
  );
}

// Brings the per-defense assignments in step with bundle teams after a plan
// write that changed bundle membership. `reconcileTeamAssignments` is pure and
// reads the assignments as they are BEFORE the write, so callers compute the
// plan from `previous` and run this only once writePlanUpdate reported
// success. Each change reuses the optimistic per-defense writers.
async function applyTeamReconcile(
  get: () => DefensePlansState,
  planId: string,
  previous: DefensePlan,
  updated: DefensePlan,
  assignments: Record<string, DefensePlanAssignment>
): Promise<void> {
  const { toCreate, toDelete } = reconcileTeamAssignments(previous, updated, assignments);
  await Promise.all([
    ...toCreate.map((c) =>
      get().setOwnerAssignment(planId, c.featureId, c.partyId, {
        status: 'asked',
        ...(c.description ? { description: c.description } : {}),
      })
    ),
    ...toDelete.map((id) => get().removeAssignment(planId, id)),
  ]);
}

export const useDefensePlansStore = create<DefensePlansState>((set, get) => ({
  ownPlans: [],
  activeOwnPlanId: null,
  draftingNewPlan: false,
  pendingTitle: null,
  ownPlansLoaded: false,
  ownPlan: null,
  areaPlans: [],
  viewedPlan: null,
  isSaving: false,
  saveError: null,
  viewedPlanNotice: null,
  partiesByPlan: {},
  assignmentsByPlan: {},
  assignmentUpdatesByPlan: {},
  bidsByPlan: {},
  fundingRequestsByPlan: {},
  unpersistedPlanIds: {},
  hiddenPartyIds: {},
  hideParty: (partyId) =>
    set((s) => ({ hiddenPartyIds: { ...s.hiddenPartyIds, [partyId]: true } })),
  unhideParty: (partyId) =>
    set((s) => {
      if (!(partyId in s.hiddenPartyIds)) return s;
      const next = { ...s.hiddenPartyIds };
      delete next[partyId];
      return { hiddenPartyIds: next };
    }),

  // Kept named subscribeOwnPlan/unsubscribeOwnPlan (not "Plans") since every
  // caller (useMapSubscriptions.ts) already calls it that way and the
  // action's job — "keep the active-plan machinery live for this uid" —
  // hasn't changed, only what it subscribes to underneath
  // (defensePlansApi.subscribeOwnPlans, plural, added when several plans
  // per owner became possible).
  subscribeOwnPlan: (uid: string) => {
    if (ownPlanUnsub && ownPlanKey === uid) return;
    ownPlanUnsub?.();
    ownPlanKey = uid;
    set({ ownPlansLoaded: false });
    ownPlanUnsub = defensePlansApi.subscribeOwnPlans(
      uid,
      (serverPlans) => {
        set((s) => {
          // A plan whose create is still queued isn't in the server's list
          // yet. Without it here, a snapshot landing in that window (the ack
          // of a delete made just before, say) reseated the owner onto
          // another plan, or onto none, and the plan in hand vanished from
          // the panel (Glenn, Oct 8 2026). The create's own success or
          // failure branch takes it out of unpersistedPlanIds.
          const pending = s.ownPlans.filter(
            (p) => s.unpersistedPlanIds[p.id] && !serverPlans.some((sp) => sp.id === p.id)
          );
          const plans = pending.length > 0 ? [...pending, ...serverPlans] : serverPlans;
          // A plan the owner is actively drawing on (viewedPlan pointing at
          // the same id) must stay in step with every snapshot — including
          // one where that plan has been deleted out from under them. This
          // snapshot is authoritative for every plan this uid owns, so "not
          // in the list any more" reliably means "gone", not "not loaded
          // yet" (unlike the single-plan version this replaced).
          const viewedPlan =
            s.viewedPlan && s.viewedPlan.ownerId === uid
              ? (plans.find((p) => p.id === s.viewedPlan!.id) ?? null)
              : s.viewedPlan;

          // Drafting a new plan (startNewPlan) means the user deliberately
          // stepped away from every existing plan — an incoming snapshot
          // must not silently reseat them onto one while they're mid-draw.
          if (s.draftingNewPlan) {
            return { ownPlans: plans, viewedPlan, ownPlansLoaded: true };
          }

          const persisted = readPersistedActivePlanId(uid);
          const activeOwnPlanId = resolveActiveOwnPlanId(plans, s.activeOwnPlanId, persisted);
          writePersistedActivePlanId(uid, activeOwnPlanId);
          const ownPlan = plans.find((p) => p.id === activeOwnPlanId) ?? null;
          return { ownPlans: plans, activeOwnPlanId, ownPlan, viewedPlan, ownPlansLoaded: true };
        });
      },
      // A background listener failure is not the editor's business — no UI
      // string, just a log. See the saveError doc comment above.
      (error) => {
        log.warn('subscribeOwnPlan failed:', error);
        // Loading ends on an error too; the panel falls back to its empty state.
        set({ ownPlansLoaded: true });
      }
    );
  },

  unsubscribeOwnPlan: () => {
    ownPlanUnsub?.();
    ownPlanUnsub = null;
    ownPlanKey = null;
    set({
      ownPlans: [],
      activeOwnPlanId: null,
      draftingNewPlan: false,
      pendingTitle: null,
      ownPlansLoaded: false,
      ownPlan: null,
    });
  },

  subscribeAreaPlans: (areaId: string) => {
    if (areaPlansUnsub && areaPlansKey === areaId) return;
    areaPlansUnsub?.();
    areaPlansKey = areaId;
    areaPlansUnsub = defensePlansApi.subscribeAreaPlans(
      areaId,
      (plans) => set({ areaPlans: plans }),
      (error) => {
        log.warn('subscribeAreaPlans failed:', error);
      }
    );
  },

  unsubscribeAreaPlans: () => {
    areaPlansUnsub?.();
    areaPlansUnsub = null;
    areaPlansKey = null;
    set({ areaPlans: [] });
  },

  subscribeViewedPlan: (planId: string) => {
    const key = planId;
    if (viewedPlanUnsub && viewedPlanKey === key) return;
    viewedPlanUnsub?.();
    viewedPlanKey = key;
    viewedPlanUnsub = defensePlansApi.subscribePlan(
      planId,
      (plan) => set({ viewedPlan: plan }),
      (error) => {
        // A stale callback from a subscription a newer subscribeViewedPlan
        // call already replaced — ignore it rather than clobber the newer
        // state (or the newer subscription's own key/unsub refs).
        if (viewedPlanKey !== key) return;
        const code = (error as { code?: string })?.code;
        if (code === 'permission-denied') {
          log.warn('subscribeViewedPlan permission-denied (plan gone or private):', error);
          viewedPlanUnsub = null;
          viewedPlanKey = null;
          set({ viewedPlan: null, viewedPlanNotice: PLAN_UNAVAILABLE_NOTICE });
          useUIStore.getState().setViewedPlanId(null);
        } else {
          log.warn('subscribeViewedPlan failed:', error);
        }
      }
    );
  },

  unsubscribeViewedPlan: () => {
    viewedPlanUnsub?.();
    viewedPlanUnsub = null;
    viewedPlanKey = null;
    set({ viewedPlan: null });
  },

  subscribeOwnPlanParties: (planId: string) => {
    if (ownPartiesUnsub && ownPartiesKey === planId) return;
    ownPartiesUnsub?.();
    ownPartiesKey = planId;
    ownPartiesUnsub = defensePlanPartiesApi.subscribeParties(
      planId,
      (parties) => set((s) => ({ partiesByPlan: { ...s.partiesByPlan, [planId]: parties } })),
      (error) => log.warn('subscribeOwnPlanParties failed:', error)
    );
  },

  unsubscribeOwnPlanParties: () => {
    ownPartiesUnsub?.();
    ownPartiesUnsub = null;
    ownPartiesKey = null;
  },

  subscribeViewedPlanParties: (planId: string) => {
    if (viewedPartiesUnsub && viewedPartiesKey === planId) return;
    viewedPartiesUnsub?.();
    viewedPartiesKey = planId;
    viewedPartiesUnsub = defensePlanPartiesApi.subscribeParties(
      planId,
      (parties) => set((s) => ({ partiesByPlan: { ...s.partiesByPlan, [planId]: parties } })),
      (error) => log.warn('subscribeViewedPlanParties failed:', error)
    );
  },

  unsubscribeViewedPlanParties: () => {
    viewedPartiesUnsub?.();
    viewedPartiesUnsub = null;
    viewedPartiesKey = null;
  },

  subscribeOwnPlanAssignments: (planId: string) => {
    if (ownAssignmentsUnsub && ownAssignmentsKey === planId) return;
    ownAssignmentsUnsub?.();
    ownAssignmentsKey = planId;
    ownAssignmentsUnsub = defensePlanPartiesApi.subscribeAssignments(
      planId,
      (assignments) =>
        set((s) => ({ assignmentsByPlan: { ...s.assignmentsByPlan, [planId]: assignments } })),
      // A background listener failure is not the editor's business — see
      // the saveError doc comment above.
      (error) => log.warn('subscribeOwnPlanAssignments failed:', error)
    );
  },

  unsubscribeOwnPlanAssignments: () => {
    ownAssignmentsUnsub?.();
    ownAssignmentsUnsub = null;
    ownAssignmentsKey = null;
  },

  subscribeViewedPlanAssignments: (planId: string) => {
    if (viewedAssignmentsUnsub && viewedAssignmentsKey === planId) return;
    viewedAssignmentsUnsub?.();
    viewedAssignmentsKey = planId;
    viewedAssignmentsUnsub = defensePlanPartiesApi.subscribeAssignments(
      planId,
      (assignments) =>
        set((s) => ({ assignmentsByPlan: { ...s.assignmentsByPlan, [planId]: assignments } })),
      (error) => log.warn('subscribeViewedPlanAssignments failed:', error)
    );
  },

  unsubscribeViewedPlanAssignments: () => {
    viewedAssignmentsUnsub?.();
    viewedAssignmentsUnsub = null;
    viewedAssignmentsKey = null;
  },

  subscribeBids: (planId: string) => {
    if (ownBidsUnsub && ownBidsKey === planId) return;
    ownBidsUnsub?.();
    ownBidsKey = planId;
    ownBidsUnsub = planBidsApi.subscribeBids(
      planId,
      (bids) => set((s) => ({ bidsByPlan: { ...s.bidsByPlan, [planId]: bids } })),
      // A background listener failure is not the editor's business — see
      // the saveError doc comment above.
      (error) => log.warn('subscribeBids failed:', error)
    );
  },

  unsubscribeBids: () => {
    ownBidsUnsub?.();
    ownBidsUnsub = null;
    ownBidsKey = null;
  },

  subscribeFundingRequests: (planId, uid) => {
    if (ownFundingUnsub && ownFundingKey === planId) return;
    ownFundingUnsub?.();
    ownFundingKey = planId;
    ownFundingUnsub = fundingRequestsApi.subscribeOwnerRequests(
      planId,
      uid,
      (requests) =>
        set((s) => ({ fundingRequestsByPlan: { ...s.fundingRequestsByPlan, [planId]: requests } })),
      (error) => log.warn('subscribeFundingRequests failed:', error)
    );
  },

  unsubscribeFundingRequests: () => {
    ownFundingUnsub?.();
    ownFundingUnsub = null;
    ownFundingKey = null;
  },

  subscribeOwnPlanAssignmentUpdates: (planId: string) => {
    if (ownAssignmentUpdatesUnsub && ownAssignmentUpdatesKey === planId) return;
    ownAssignmentUpdatesUnsub?.();
    ownAssignmentUpdatesKey = planId;
    ownAssignmentUpdatesUnsub = subscribeAssignmentUpdates(
      planId,
      (updates) =>
        set((s) => ({
          assignmentUpdatesByPlan: { ...s.assignmentUpdatesByPlan, [planId]: updates },
        })),
      // A background listener failure is not the editor's business — see
      // the saveError doc comment above.
      (error) => log.warn('subscribeOwnPlanAssignmentUpdates failed:', error)
    );
  },

  unsubscribeOwnPlanAssignmentUpdates: () => {
    ownAssignmentUpdatesUnsub?.();
    ownAssignmentUpdatesUnsub = null;
    ownAssignmentUpdatesKey = null;
  },

  addFeature: async (input: AddFeatureInput) => {
    const feature: DefensePlanFeature = {
      id: input.id ?? newFeatureId(),
      strategy: input.strategy,
      kind: input.kind,
      coords: input.coords,
      structureId: input.structureId,
      address: input.address,
      bundleId: input.bundleId,
      createdAt: Timestamp.now(),
    };

    // A fresh draw gets its entrance on the map and in the list. An undo
    // passes the deleted feature's own id, and a restore isn't a draw.
    if (!input.id) markPlanFeatureAddedLocally(feature.id);

    const { ownPlan } = get();

    // `ownPlan` stays a real (if empty) plan once one exists — see
    // removeFeature's comment on why the doc is never deleted just because
    // its last feature was. So this branch is "no plan has ever been
    // created", not "the plan is empty", and the undo of a just-deleted
    // last feature (DefensePlanPanel's handleUndo, which calls this with
    // the deleted feature's own shape) lands in the append branch below,
    // back into the same doc rather than minting a new plan id.
    if (!ownPlan) {
      const user = useAuthStore.getState().user;
      if (!user) {
        // Both entry points gate drawing behind requireAuth (B6), so this
        // is a defensive fallback, not a path the UI is expected to hit.
        set({ saveError: 'Sign in to draw a defense plan.' });
        return;
      }
      const { lat, lng } = input.coords[0];
      const areaId = useServiceAreasStore.getState().findContaining(lat, lng)?.id ?? 'unassigned';
      const { ownerName } = ownerIdentityFor(user);
      const { ownPlans } = get();
      // A title the owner typed before drawing wins over the default, and
      // isn't made unique: it's their choice.
      const typedTitle = get().pendingTitle;
      const title =
        typedTitle ??
        uniquePlanTitle(
          defaultNewPlanTitle(ownPlans.length, user, placeForNewPlan(input)),
          ownPlans.map((p) => p.title)
        );
      const planId = defensePlansApi.newPlanId();
      const now = Timestamp.now();
      const optimisticPlan: DefensePlan = {
        id: planId,
        ownerId: user.id,
        ownerName,
        areaId,
        title,
        // Sept 17 2026 (product decision): a new plan starts private — the
        // owner opts into sharing later via the visibility toggle, rather
        // than every plan defaulting to world-readable.
        visibility: 'private',
        features: [feature],
        featureCount: 1,
        bounds: planBounds([feature]),
        bundles: [],
        createdAt: now,
        updatedAt: now,
      };

      // Captured before any optimistic mutation, so a failed create can put
      // the active selection and draftingNewPlan back exactly as they were
      // (draftingNewPlan is cleared optimistically below — see the
      // interface doc comment on startNewPlan for why clearing early rather
      // than only "when the plan arrives" is fine). ownPlans itself is
      // reverted by filtering the doc that never landed out of whatever the
      // list looks like by the time the write settles, not by restoring
      // this snapshot — a concurrent subscription update in between must
      // not be undone.
      const previousActiveId = get().activeOwnPlanId;
      const previousDrafting = get().draftingNewPlan;

      set((s) => ({
        ownPlans: [optimisticPlan, ...s.ownPlans],
        activeOwnPlanId: planId,
        draftingNewPlan: false,
        pendingTitle: null,
        ownPlan: optimisticPlan,
        viewedPlan: syncViewed(s.viewedPlan, planId, optimisticPlan),
        isSaving: true,
        saveError: null,
        unpersistedPlanIds: { ...s.unpersistedPlanIds, [planId]: true },
      }));
      writePersistedActivePlanId(user.id, planId);

      await enqueueWrite(async () => {
        try {
          const created = await defensePlansApi.createPlan(planId, {
            ownerId: user.id,
            ownerName,
            areaId,
            title,
            features: [feature],
            visibility: 'private',
          });
          set((s) => {
            const unpersistedPlanIds = { ...s.unpersistedPlanIds };
            delete unpersistedPlanIds[planId];
            return {
              isSaving: false,
              ownPlans: replacePlanInList(s.ownPlans, created),
              ownPlan: s.activeOwnPlanId === planId ? created : s.ownPlan,
              viewedPlan: syncViewed(s.viewedPlan, planId, created),
              unpersistedPlanIds,
            };
          });
        } catch (err) {
          log.error('createPlan failed:', err);
          set((s) => {
            // The doc never landed, so it comes back out of ownPlans
            // regardless of what's active now. Only roll the active
            // selection itself back if nothing else changed it meanwhile
            // (e.g. the user switched plans while this write was pending).
            const stillActiveHere = s.activeOwnPlanId === planId;
            const revertedOwnPlans = s.ownPlans.filter((p) => p.id !== planId);
            const unpersistedPlanIds = { ...s.unpersistedPlanIds };
            delete unpersistedPlanIds[planId];
            return {
              ownPlans: revertedOwnPlans,
              activeOwnPlanId: stillActiveHere ? previousActiveId : s.activeOwnPlanId,
              draftingNewPlan: stillActiveHere ? previousDrafting : s.draftingNewPlan,
              pendingTitle: stillActiveHere ? typedTitle : s.pendingTitle,
              unpersistedPlanIds,
              ownPlan: stillActiveHere
                ? (revertedOwnPlans.find((p) => p.id === previousActiveId) ?? null)
                : s.ownPlan,
              viewedPlan: syncViewed(s.viewedPlan, planId, null),
              isSaving: false,
              saveError: getErrorMessage(err),
            };
          });
        }
      });
      return;
    }

    const previous = ownPlan;
    let updated: DefensePlan;
    try {
      updated = applyAddFeature(ownPlan, feature);
    } catch (err) {
      set({ saveError: getErrorMessage(err) });
      return;
    }

    set((s) => ({
      ownPlans: replacePlanInList(s.ownPlans, updated),
      ownPlan: updated,
      viewedPlan: syncViewed(s.viewedPlan, updated.id, updated),
      isSaving: true,
      saveError: null,
    }));

    let addedToBundle = false;
    try {
      const outcome = await writePlanDoc(updated.id, {
        features: updated.features,
        featureCount: updated.featureCount,
        bounds: updated.bounds,
        bundles: updated.bundles,
      });
      if (outcome === 'written') set({ isSaving: false });
      // A defense that lands in a bundle (a copy of a bundled home, or an
      // undo of a delete) picks up the bundle's team. Runs after the
      // write succeeded, outside the try so a failed link can't roll the
      // feature back.
      addedToBundle = !!feature.bundleId;
    } catch (err) {
      log.error('writeFeatures (add) failed:', err);
      set((s) => ({
        ownPlans: replacePlanInList(s.ownPlans, previous),
        ownPlan: s.ownPlan?.id === updated.id ? previous : s.ownPlan,
        viewedPlan: syncViewed(s.viewedPlan, updated.id, previous),
        isSaving: false,
        saveError: getErrorMessage(err),
      }));
    }
    if (addedToBundle) {
      await applyTeamReconcile(
        get,
        updated.id,
        previous,
        updated,
        get().assignmentsByPlan[updated.id] ?? {}
      );
    }
  },

  copyFeatureAs: (featureId, strategy) => {
    const source = get().ownPlan?.features.find((f) => f.id === featureId);
    if (!source) return null;
    const id = newFeatureId();
    // addFeature skips the entrance for a caller-supplied id (that is how it
    // tells an undo restore from a draw), and a copy is a draw.
    markPlanFeatureAddedLocally(id);
    void get().addFeature({
      id,
      strategy,
      kind: source.kind,
      coords: source.coords.map((c) => ({ ...c })),
      structureId: source.structureId,
      address: source.address,
      bundleId: source.bundleId,
    });
    // addFeature puts the feature in the plan before its first await, so
    // this tells a refused add (the cap) from a copy that is on its way.
    const added = get().ownPlan?.features.some((f) => f.id === id);
    return added ? id : null;
  },

  // Deliberately never deletes the plan doc, even when this empties
  // `features` — an id kept alive is what lets undo (DefensePlanPanel's
  // handleUndo -> addFeature) put the feature straight back into the same
  // doc instead of minting a new plan id, which would silently break any
  // /plan/<id> link already shared for this plan. A 0-feature doc is a
  // normal, schema-valid state (DefensePlanSchema.features has no .min(),
  // the rules impose no minimum either) — the UI treats it as "no plan
  // yet" (DefensePlanPanel's headerMetaText/footer, MapLayersSheet's
  // shared-plans list, suggestionsForPoint) without the doc having to
  // agree by not existing.
  removeFeature: async (featureId: string) => {
    const { ownPlan } = get();
    if (!ownPlan) return;
    const removed = applyRemoveFeature(ownPlan, featureId);
    if (removed === ownPlan) return; // no-op: feature already gone
    // A deleted feature's bundle (if it had one) may now be empty — drop it
    // rather than leave a bundle header with nothing under it.
    const updated: DefensePlan = {
      ...removed,
      bundles: pruneEmptyBundles(
        ownPlan.bundles,
        removed.features,
        keptBundleIds(ownPlan.id, ownPlan.bundles)
      ),
    };

    const previous = ownPlan;
    set((s) => ({
      ownPlans: replacePlanInList(s.ownPlans, updated),
      ownPlan: updated,
      viewedPlan: syncViewed(s.viewedPlan, updated.id, updated),
      isSaving: true,
      saveError: null,
    }));

    try {
      const outcome = await writePlanDoc(updated.id, {
        features: updated.features,
        featureCount: updated.featureCount,
        bounds: updated.bounds,
        bundles: updated.bundles,
      });
      if (outcome === 'written') set({ isSaving: false });
      // Best effort: a deleted feature no longer needs "who's
      // responsible" links, though the parties themselves stay (they may
      // cover other defenses too). Reads the live-subscribed assignments
      // rather than querying, so this only cleans up what's currently
      // known locally — acceptable for a best-effort pass whose failure
      // is only logged, since the feature delete itself already
      // succeeded and an orphaned link is harmless (no feature id points
      // at it any more, so no row ever reads it).
      const linkedIds = Object.values(get().assignmentsByPlan[updated.id] ?? {})
        .filter((a) => a.featureId === featureId)
        .map((a) => a.id);
      void Promise.all(
        linkedIds.map((id) => defensePlanPartiesApi.deleteAssignment(updated.id, id))
      ).catch((err) => log.warn('best-effort assignment cleanup failed:', err));
    } catch (err) {
      log.error('writeFeatures (remove) failed:', err);
      set((s) => ({
        ownPlans: replacePlanInList(s.ownPlans, previous),
        ownPlan: s.ownPlan?.id === updated.id ? previous : s.ownPlan,
        viewedPlan: syncViewed(s.viewedPlan, updated.id, previous),
        isSaving: false,
        saveError: getErrorMessage(err),
      }));
    }
  },

  updateTitle: async (title: string) => {
    const { ownPlan } = get();
    if (!ownPlan) {
      // No doc yet: keep the title until the first defense creates it.
      set({ pendingTitle: title });
      return;
    }
    const previous = ownPlan;
    const updated: DefensePlan = { ...ownPlan, title };

    set((s) => ({
      ownPlans: replacePlanInList(s.ownPlans, updated),
      ownPlan: updated,
      viewedPlan: syncViewed(s.viewedPlan, updated.id, updated),
      isSaving: true,
      saveError: null,
    }));

    await enqueueWrite(async () => {
      try {
        await defensePlansApi.updateTitle(updated.id, title);
        set({ isSaving: false });
      } catch (err) {
        log.error('updateTitle failed:', err);
        set((s) => ({
          ownPlans: replacePlanInList(s.ownPlans, previous),
          ownPlan: s.ownPlan?.id === updated.id ? previous : s.ownPlan,
          viewedPlan: syncViewed(s.viewedPlan, updated.id, previous),
          isSaving: false,
          saveError: getErrorMessage(err),
        }));
      }
    });
  },

  setVisibility: async (visibility: PlanVisibility) => {
    const { ownPlan } = get();
    if (!ownPlan) return false;
    let saved = false;
    const previous = ownPlan;
    const updated: DefensePlan = { ...ownPlan, visibility };

    set((s) => ({
      ownPlans: replacePlanInList(s.ownPlans, updated),
      ownPlan: updated,
      viewedPlan: syncViewed(s.viewedPlan, updated.id, updated),
      isSaving: true,
      saveError: null,
    }));

    await enqueueWrite(async () => {
      try {
        await defensePlansApi.setVisibility(updated.id, visibility);
        set({ isSaving: false });
        saved = true;
      } catch (err) {
        log.error('setVisibility failed:', err);
        set((s) => ({
          ownPlans: replacePlanInList(s.ownPlans, previous),
          ownPlan: s.ownPlan?.id === updated.id ? previous : s.ownPlan,
          viewedPlan: syncViewed(s.viewedPlan, updated.id, previous),
          isSaving: false,
          saveError: getErrorMessage(err),
        }));
      }
    });
    return saved;
  },

  deleteOwnPlan: async (planId?: string) => {
    const state = get();
    const targetId = planId ?? state.activeOwnPlanId;
    if (!targetId) return;
    const target =
      state.ownPlans.find((p) => p.id === targetId) ??
      (state.ownPlan?.id === targetId ? state.ownPlan : null);
    if (!target) return;

    const previousOwnPlans = state.ownPlans;
    const previousActiveId = state.activeOwnPlanId;
    const previousOwnPlan = state.ownPlan;
    const previousDrafting = state.draftingNewPlan;
    const wasActive = previousActiveId === targetId;

    const remaining = previousOwnPlans.filter((p) => p.id !== targetId);
    // Falls back to the newest remaining plan (or null when none are
    // left), same resolution newestPlan gives a fresh subscription.
    const nextActiveId = wasActive ? (newestPlan(remaining)?.id ?? null) : previousActiveId;
    const nextOwnPlan = wasActive
      ? (remaining.find((p) => p.id === nextActiveId) ?? null)
      : previousOwnPlan;

    set((s) => ({
      ownPlans: remaining,
      activeOwnPlanId: nextActiveId,
      draftingNewPlan: wasActive ? false : s.draftingNewPlan,
      ownPlan: nextOwnPlan,
      viewedPlan: syncViewed(s.viewedPlan, targetId, null),
      isSaving: true,
      saveError: null,
    }));
    if (wasActive && ownPlanKey) writePersistedActivePlanId(ownPlanKey, nextActiveId);

    await enqueueWrite(async () => {
      try {
        await defensePlansApi.deletePlan(targetId);
        set({ isSaving: false });
      } catch (err) {
        log.error('deletePlan failed:', err);
        set((s) => ({
          ownPlans: previousOwnPlans,
          activeOwnPlanId: previousActiveId,
          draftingNewPlan: previousDrafting,
          ownPlan: previousOwnPlan,
          viewedPlan: syncViewed(s.viewedPlan, targetId, target),
          isSaving: false,
          saveError: getErrorMessage(err),
        }));
        if (wasActive && ownPlanKey) writePersistedActivePlanId(ownPlanKey, previousActiveId);
      }
    });
  },

  setActiveOwnPlan: (planId: string) => {
    const { ownPlans } = get();
    const plan = ownPlans.find((p) => p.id === planId);
    if (!plan) return; // not one of this owner's plans — ignore
    set({ activeOwnPlanId: planId, draftingNewPlan: false, pendingTitle: null, ownPlan: plan });
    useUIStore.getState().setLastPlanCopy(null);
    if (ownPlanKey) writePersistedActivePlanId(ownPlanKey, planId);
  },

  startNewPlan: () => {
    set({ activeOwnPlanId: null, draftingNewPlan: true, pendingTitle: null, ownPlan: null });
    useUIStore.getState().setLastPlanCopy(null);
  },

  duplicateActivePlan: async () => {
    const { ownPlan } = get();
    if (!ownPlan) return;
    const user = useAuthStore.getState().user;
    if (!user) {
      set({ saveError: 'Sign in to duplicate a plan.' });
      return;
    }

    const planId = defensePlansApi.newPlanId();
    const title = `Copy of ${ownPlan.title}`.slice(0, 80);
    // Whole-object spread (not a field whitelist) so an in-flight schema
    // change that adds fields to DefensePlanFeature (bundleId, structureId,
    // address) is copied along for free.
    const features = ownPlan.features.map((f) => ({ ...f }));
    const now = Timestamp.now();
    const optimisticPlan: DefensePlan = {
      ...ownPlan,
      id: planId,
      title,
      features,
      featureCount: features.length,
      // The copy starts with no parties, so a copied team's party ids would
      // dangle ("Unknown party"). Bundles keep their names and members.
      // Nor is it open for bids: its bids stayed with the original, and
      // nobody has been invited to the copy.
      bundles: stripInvitedVendors(stripBundleTeams(ownPlan.bundles)).map(
        ({ bidsOpen: _open, bidsOpenedAt: _openedAt, ...bundle }) => bundle
      ),
      createdAt: now,
      updatedAt: now,
    };

    const previousActiveId = get().activeOwnPlanId;
    const previousDrafting = get().draftingNewPlan;

    set((s) => ({
      ownPlans: [optimisticPlan, ...s.ownPlans],
      activeOwnPlanId: planId,
      draftingNewPlan: false,
      ownPlan: optimisticPlan,
      isSaving: true,
      saveError: null,
      unpersistedPlanIds: { ...s.unpersistedPlanIds, [planId]: true },
    }));
    writePersistedActivePlanId(user.id, planId);

    await enqueueWrite(async () => {
      try {
        const created = await defensePlansApi.createPlan(planId, {
          ownerId: user.id,
          ownerName: ownPlan.ownerName,
          areaId: ownPlan.areaId,
          title,
          features,
          visibility: ownPlan.visibility,
          bundles: optimisticPlan.bundles,
        });
        set((s) => {
          const unpersistedPlanIds = { ...s.unpersistedPlanIds };
          delete unpersistedPlanIds[planId];
          return {
            isSaving: false,
            ownPlans: replacePlanInList(s.ownPlans, created),
            ownPlan: s.activeOwnPlanId === planId ? created : s.ownPlan,
            unpersistedPlanIds,
          };
        });
      } catch (err) {
        log.error('duplicateActivePlan (createPlan) failed:', err);
        set((s) => {
          const stillActiveHere = s.activeOwnPlanId === planId;
          const revertedOwnPlans = s.ownPlans.filter((p) => p.id !== planId);
          const unpersistedPlanIds = { ...s.unpersistedPlanIds };
          delete unpersistedPlanIds[planId];
          return {
            ownPlans: revertedOwnPlans,
            activeOwnPlanId: stillActiveHere ? previousActiveId : s.activeOwnPlanId,
            draftingNewPlan: stillActiveHere ? previousDrafting : s.draftingNewPlan,
            unpersistedPlanIds,
            ownPlan: stillActiveHere
              ? (revertedOwnPlans.find((p) => p.id === previousActiveId) ?? null)
              : s.ownPlan,
            isSaving: false,
            saveError: getErrorMessage(err),
          };
        });
      }
    });
  },

  createBundle: async (name: string, featureIds: string[]) => {
    const { ownPlan } = get();
    if (!ownPlan) return null;
    const trimmed = name.trim();
    if (!trimmed || featureIds.length === 0) return null;
    if (ownPlan.bundles.length >= MAX_BUNDLES_PER_PLAN) {
      set({ saveError: `A plan can hold at most ${MAX_BUNDLES_PER_PLAN} bundles.` });
      return null;
    }
    const planId = ownPlan.id;
    const bundle: PlanBundle = { id: newBundleId(), name: trimmed, createdAt: Timestamp.now() };
    const features = applyBundleMembership(ownPlan.features, bundle.id, featureIds);
    // Moving these features out of whatever bundle(s) they were in before
    // may have emptied one — drop it the same way removeFeature does.
    const bundles = pruneEmptyBundles(
      [...ownPlan.bundles, bundle],
      features,
      keptBundleIds(planId, ownPlan.bundles)
    );
    const updated: DefensePlan = { ...ownPlan, features, bundles };
    // Read before the write: the pure reconcile derives teams from the
    // assignments and the plan as they were. A defense moving out of a bundle
    // that has a team loses that team's links.
    const assignments = get().assignmentsByPlan[planId] ?? {};
    const ok = await writePlanUpdate(set, planId, ownPlan, updated, 'createBundle');
    if (!ok) return null;
    await applyTeamReconcile(get, planId, ownPlan, updated, assignments);
    return bundle.id;
  },

  moveFeaturesToBundle: async (featureIds: string[], bundleId: string | null) => {
    const { ownPlan } = get();
    if (!ownPlan) return null;
    if (bundleId !== null && !ownPlan.bundles.some((b) => b.id === bundleId)) return null;
    const present = new Set(ownPlan.features.map((f) => f.id));
    const ids = featureIds.filter((id) => present.has(id));
    if (ids.length === 0) return null;
    const planId = ownPlan.id;
    const moved = moveToBundle(
      ownPlan.features,
      ownPlan.bundles,
      ids,
      bundleId,
      keptBundleIds(planId, ownPlan.bundles)
    );
    if (moved.features === ownPlan.features) return null;
    const idSet = new Set(ids);
    const snapshot: MembershipSnapshot = {
      planId,
      previous: ownPlan.features
        .filter((f) => idSet.has(f.id))
        .map((f) => (f.bundleId ? { featureId: f.id, bundleId: f.bundleId } : { featureId: f.id })),
      bundles: ownPlan.bundles,
    };
    const updated: DefensePlan = { ...ownPlan, features: moved.features, bundles: moved.bundles };
    const assignments = get().assignmentsByPlan[planId] ?? {};
    const ok = await writePlanUpdate(set, planId, ownPlan, updated, 'moveFeaturesToBundle');
    if (!ok) return null;
    await applyTeamReconcile(get, planId, ownPlan, updated, assignments);
    return snapshot;
  },

  restoreBundleMembership: async (snapshot: MembershipSnapshot) => {
    const { ownPlan } = get();
    if (!ownPlan || ownPlan.id !== snapshot.planId) return;
    const planId = ownPlan.id;
    const previousById = new Map(snapshot.previous.map((p) => [p.featureId, p.bundleId]));
    const features = ownPlan.features.map((feature) => {
      if (!previousById.has(feature.id)) return feature;
      const before = previousById.get(feature.id);
      if (before === feature.bundleId) return feature;
      if (before) return { ...feature, bundleId: before };
      const { bundleId: _drop, ...rest } = feature;
      return rest as typeof feature;
    });
    const inSnapshot = new Set(snapshot.bundles.map((b) => b.id));
    const created = ownPlan.bundles.filter((b) => !inSnapshot.has(b.id));
    const bundles = pruneEmptyBundles(
      [...snapshot.bundles, ...created],
      features,
      keptBundleIds(planId, ownPlan.bundles)
    );
    if (bundles.length > MAX_BUNDLES_PER_PLAN) {
      set({ saveError: `A plan can hold at most ${MAX_BUNDLES_PER_PLAN} bundles.` });
      return;
    }
    const updated: DefensePlan = { ...ownPlan, features, bundles };
    const assignments = get().assignmentsByPlan[planId] ?? {};
    const ok = await writePlanUpdate(set, planId, ownPlan, updated, 'restoreBundleMembership');
    if (ok) await applyTeamReconcile(get, planId, ownPlan, updated, assignments);
  },

  restoreBundle: async (bundle: PlanBundle, featureIds: string[]) => {
    const { ownPlan } = get();
    if (!ownPlan) return;
    if (ownPlan.bundles.some((b) => b.id === bundle.id)) return;
    if (ownPlan.bundles.length >= MAX_BUNDLES_PER_PLAN) return;
    const present = new Set(ownPlan.features.map((f) => f.id));
    const ids = featureIds.filter((id) => present.has(id));
    if (ids.length === 0) return;
    const planId = ownPlan.id;
    const features = applyBundleMembership(ownPlan.features, bundle.id, ids);
    const bundles = pruneEmptyBundles(
      [...ownPlan.bundles, bundle],
      features,
      keptBundleIds(planId, ownPlan.bundles)
    );
    const updated: DefensePlan = { ...ownPlan, features, bundles };
    const assignments = get().assignmentsByPlan[planId] ?? {};
    // Undo of an ungroup: the team comes back on the bundle, and the members'
    // links were never removed (ungroupBundle keeps them), so this normally
    // creates nothing. It still tops up a member that lost its links meanwhile.
    const ok = await writePlanUpdate(set, planId, ownPlan, updated, 'restoreBundle');
    if (ok) await applyTeamReconcile(get, planId, ownPlan, updated, assignments);
  },

  renameBundle: async (bundleId: string, name: string) => {
    const { ownPlan } = get();
    if (!ownPlan) return;
    const trimmed = name.trim();
    if (!trimmed) return;
    const planId = ownPlan.id;
    const bundles = ownPlan.bundles.map((b) => (b.id === bundleId ? { ...b, name: trimmed } : b));
    const updated: DefensePlan = { ...ownPlan, bundles };
    await writePlanUpdate(set, planId, ownPlan, updated, 'renameBundle');
  },

  ungroupBundle: async (bundleId: string) => {
    const { ownPlan } = get();
    if (!ownPlan) return;
    const planId = ownPlan.id;
    // Empty target set: applyBundleMembership adds no new members and
    // clears bundleId off every current one — exactly "disband".
    // The per-defense assignments are deliberately left alone: the people
    // stay on the defenses they were linked to, and undo (restoreBundle) gets
    // the team back intact because the links it relies on never went away.
    const features = applyBundleMembership(ownPlan.features, bundleId, []);
    const bundles = ownPlan.bundles.filter((b) => b.id !== bundleId);
    const updated: DefensePlan = { ...ownPlan, features, bundles };
    await writePlanUpdate(set, planId, ownPlan, updated, 'ungroupBundle');
  },

  setBundleMembers: async (bundleId: string, featureIds: string[]) => {
    const { ownPlan } = get();
    if (!ownPlan) return;
    const planId = ownPlan.id;
    const features = applyBundleMembership(ownPlan.features, bundleId, featureIds);
    // An empty featureIds list disbands the bundle the same way
    // ungroupBundle does — pruneEmptyBundles drops it once nothing
    // references it, with no separate "delete if empty" branch needed here.
    // A bundle with an upcoming event stays, so the event keeps its bundle.
    const bundles = pruneEmptyBundles(
      ownPlan.bundles,
      features,
      keptBundleIds(planId, ownPlan.bundles)
    );
    const updated: DefensePlan = { ...ownPlan, features, bundles };
    const assignments = get().assignmentsByPlan[planId] ?? {};
    const ok = await writePlanUpdate(set, planId, ownPlan, updated, 'setBundleMembers');
    if (ok) await applyTeamReconcile(get, planId, ownPlan, updated, assignments);
  },

  addBundleTeammate: async (bundleId, partyId, data) => {
    const { ownPlan } = get();
    if (!ownPlan) return;
    const bundle = ownPlan.bundles.find((b) => b.id === bundleId);
    if (!bundle) return;
    const planId = ownPlan.id;
    const members = ownPlan.features.filter((f) => f.bundleId === bundleId);
    const assignments = get().assignmentsByPlan[planId] ?? {};
    // Persist the derived team on this first edit (see effectiveBundleTeam),
    // so the stored team starts from what the owner saw on screen.
    const team = effectiveBundleTeam(bundle, members, assignments);
    const already = team.some((t) => t.partyId === partyId);
    if (!already && team.length >= MAX_BUNDLE_TEAM) {
      set({ saveError: `A bundle can have at most ${MAX_BUNDLE_TEAM} people.` });
      return;
    }
    const entry = data.description ? { partyId, description: data.description } : { partyId };
    const nextTeam = already
      ? team.map((t) => (t.partyId === partyId ? entry : t))
      : [...team, entry];
    // One leader or none: marking this person leader replaces whoever was.
    const leader = data.leader ? partyId : effectiveBundleLeader(bundle, team);
    const bundles = ownPlan.bundles.map((b) =>
      b.id === bundleId ? withBundleTeam(b, nextTeam, leader) : b
    );
    const updated: DefensePlan = { ...ownPlan, bundles };
    const ok = await writePlanUpdate(set, planId, ownPlan, updated, 'addBundleTeammate');
    if (!ok) return;
    // Each call is its own enqueueWrite'd, optimistic setOwnerAssignment, so
    // the per-defense links the update loop, emails and CSV read stay in step.
    const link = {
      status: data.status,
      ...(data.description ? { description: data.description } : {}),
    };
    await Promise.all(members.map((m) => get().setOwnerAssignment(planId, m.id, partyId, link)));
  },

  removeBundleTeammate: async (bundleId, partyId) => {
    const { ownPlan } = get();
    if (!ownPlan) return;
    const bundle = ownPlan.bundles.find((b) => b.id === bundleId);
    if (!bundle) return;
    const planId = ownPlan.id;
    const members = ownPlan.features.filter((f) => f.bundleId === bundleId);
    const assignments = get().assignmentsByPlan[planId] ?? {};
    const team = effectiveBundleTeam(bundle, members, assignments);
    if (!team.some((t) => t.partyId === partyId)) return;
    const nextTeam = team.filter((t) => t.partyId !== partyId);
    const leader = effectiveBundleLeader(bundle, nextTeam);
    const bundles = ownPlan.bundles.map((b) =>
      b.id === bundleId ? withBundleTeam(b, nextTeam, leader) : b
    );
    const updated: DefensePlan = { ...ownPlan, bundles };
    const ok = await writePlanUpdate(set, planId, ownPlan, updated, 'removeBundleTeammate');
    if (!ok) return;
    const memberIds = new Set(members.map((m) => m.id));
    const linkIds = Object.values(assignments)
      .filter((a) => a.partyId === partyId && memberIds.has(a.featureId))
      .map((a) => a.id);
    await Promise.all(linkIds.map((id) => get().removeAssignment(planId, id)));
  },

  setBundleLeader: async (bundleId, partyId) => {
    const { ownPlan } = get();
    if (!ownPlan) return;
    const bundle = ownPlan.bundles.find((b) => b.id === bundleId);
    if (!bundle) return;
    const planId = ownPlan.id;
    const members = ownPlan.features.filter((f) => f.bundleId === bundleId);
    const team = effectiveBundleTeam(bundle, members, get().assignmentsByPlan[planId] ?? {});
    // The leader must be a teammate.
    if (partyId && !team.some((t) => t.partyId === partyId)) return;
    const bundles = ownPlan.bundles.map((b) =>
      b.id === bundleId ? withBundleTeam(b, team, partyId ?? undefined) : b
    );
    const updated: DefensePlan = { ...ownPlan, bundles };
    await writePlanUpdate(set, planId, ownPlan, updated, 'setBundleLeader');
  },

  setBundleTeammateDescription: async (bundleId, partyId, description) => {
    const { ownPlan } = get();
    if (!ownPlan) return;
    const bundle = ownPlan.bundles.find((b) => b.id === bundleId);
    if (!bundle) return;
    const planId = ownPlan.id;
    const members = ownPlan.features.filter((f) => f.bundleId === bundleId);
    const assignments = get().assignmentsByPlan[planId] ?? {};
    const team = effectiveBundleTeam(bundle, members, assignments);
    if (!team.some((t) => t.partyId === partyId)) return;
    const entry = description ? { partyId, description } : { partyId };
    const nextTeam = team.map((t) => (t.partyId === partyId ? entry : t));
    const leader = effectiveBundleLeader(bundle, team);
    const bundles = ownPlan.bundles.map((b) =>
      b.id === bundleId ? withBundleTeam(b, nextTeam, leader) : b
    );
    const ok = await writePlanUpdate(
      set,
      planId,
      ownPlan,
      { ...ownPlan, bundles },
      'setBundleTeammateDescription'
    );
    if (!ok) return;
    const memberIds = new Set(members.map((m) => m.id));
    const links = Object.values(assignments).filter(
      (a) => a.partyId === partyId && memberIds.has(a.featureId)
    );
    await Promise.all(
      links.map((a) =>
        get().setOwnerAssignment(planId, a.featureId, partyId, {
          status: a.status,
          ...(description ? { description } : {}),
        })
      )
    );
  },

  setBundleBidsOpen: async (planId, bundleId, open) => {
    const { ownPlan } = get();
    if (!ownPlan || ownPlan.id !== planId) return false;
    const bundle = ownPlan.bundles.find((b) => b.id === bundleId);
    if (!bundle || !!bundle.bidsOpen === open) return !!bundle;
    const bundles = ownPlan.bundles.map((b) =>
      b.id === bundleId
        ? open
          ? { ...b, bidsOpen: true, bidsOpenedAt: Timestamp.now() }
          : { ...b, bidsOpen: false }
        : b
    );
    return writePlanUpdate(set, planId, ownPlan, { ...ownPlan, bundles }, 'setBundleBidsOpen');
  },

  toggleInvitedVendor: async (bundleId, entry) => {
    const { ownPlan } = get();
    if (!ownPlan) return;
    const bundle = ownPlan.bundles.find((b) => b.id === bundleId);
    if (!bundle) return;
    const next = withInvitedVendorToggled(bundle, entry, MAX_BUNDLE_INVITED_VENDORS);
    if (next === bundle) {
      set({
        saveError: `A bundle can invite at most ${MAX_BUNDLE_INVITED_VENDORS} vendors.`,
      });
      return;
    }
    const bundles = ownPlan.bundles.map((b) => (b.id === bundleId ? next : b));
    await writePlanUpdate(set, ownPlan.id, ownPlan, { ...ownPlan, bundles }, 'toggleInvitedVendor');
  },

  markVendorInvited: async (bundleId, vendorId) => {
    const { ownPlan } = get();
    if (!ownPlan) return;
    const bundle = ownPlan.bundles.find((b) => b.id === bundleId);
    if (!bundle) return;
    const next = withVendorInvitedAt(bundle, vendorId, Timestamp.now());
    if (next === bundle) return;
    const bundles = ownPlan.bundles.map((b) => (b.id === bundleId ? next : b));
    await writePlanUpdate(set, ownPlan.id, ownPlan, { ...ownPlan, bundles }, 'markVendorInvited');
  },

  requestFunding: async (planId, bundleId, bidId, amount) => {
    try {
      await fundingRequestsApi.requestFunding({ planId, bundleId, bidId, amount });
      return null;
    } catch (err) {
      log.warn('requestFunding failed:', err);
      return toFundingError(err);
    }
  },

  withdrawFundingRequest: async (requestId) => {
    try {
      await fundingRequestsApi.reviewFundingRequest({ requestId, action: 'withdraw' });
      return null;
    } catch (err) {
      log.warn('withdrawFundingRequest failed:', err);
      return toFundingError(err);
    }
  },

  setBundleEstimate: async (planId, bundleId, estimate) => {
    const { ownPlan } = get();
    if (!ownPlan || ownPlan.id !== planId) return false;
    const bundle = ownPlan.bundles.find((b) => b.id === bundleId);
    if (!bundle) return false;
    if ((bundle.estimate ?? null) === estimate) return true;
    const bundles = ownPlan.bundles.map((b) => {
      if (b.id !== bundleId) return b;
      // Firestore refuses undefined inside an array element, so drop the key.
      const { estimate: _old, ...rest } = b;
      return estimate == null ? rest : { ...rest, estimate };
    });
    return writePlanUpdate(set, planId, ownPlan, { ...ownPlan, bundles }, 'setBundleEstimate');
  },

  setBundleWorkDone: async (planId, bundleId, done) => {
    const { ownPlan } = get();
    if (!ownPlan || ownPlan.id !== planId) return false;
    const bundles = ownPlan.bundles.map((b) => {
      if (b.id !== bundleId) return b;
      const { workDoneAt: _old, ...rest } = b;
      return done ? { ...rest, workDoneAt: Timestamp.now() } : rest;
    });
    return writePlanUpdate(set, planId, ownPlan, { ...ownPlan, bundles }, 'setBundleWorkDone');
  },

  acceptBid: async (planId, bidId) => {
    const { ownPlan } = get();
    const bid = (get().bidsByPlan[planId] ?? []).find((b) => b.id === bidId);
    if (!ownPlan || ownPlan.id !== planId || !bid || bid.status !== 'submitted') return false;
    if (!ownPlan.bundles.some((b) => b.id === bid.bundleId)) {
      set({ saveError: 'That bundle is no longer on the plan.' });
      return false;
    }
    // 1. The vendor as a party, with the bid's email as its private contact,
    // so Ask for an update reaches them like any other teammate.
    const partyId = await get().createOwnerParty(
      planId,
      { type: 'vendor', organization: bid.organization, person: bid.person },
      bid.email
    );
    if (!partyId) return false;
    // 2. On the bundle team, linked to every member defense.
    await get().addBundleTeammate(bid.bundleId, partyId, {
      status: 'accepted',
      description: bidTeamDescription(bid),
    });
    const onTeam = get()
      .ownPlan?.bundles.find((b) => b.id === bid.bundleId)
      ?.team?.some((t) => t.partyId === partyId);
    if (!onTeam) {
      // A full team or a failed write (saveError says which). Don't leave a
      // vendor party behind that nothing points at.
      await get().deleteOwnerParty(planId, partyId);
      return false;
    }
    // 3. Only then is the bid accepted.
    return writeBidStatus(set, planId, bidId, 'accepted', partyId);
  },

  submitBid: (request) => submitBid(request),

  declineBid: async (planId, bidId) => {
    const bid = (get().bidsByPlan[planId] ?? []).find((b) => b.id === bidId);
    if (!bid || bid.status !== 'submitted') return false;
    return writeBidStatus(set, planId, bidId, 'declined');
  },

  setBundleTeammateStatus: async (bundleId, partyId, status) => {
    const { ownPlan } = get();
    if (!ownPlan) return;
    const planId = ownPlan.id;
    const memberIds = new Set(
      ownPlan.features.filter((f) => f.bundleId === bundleId).map((f) => f.id)
    );
    const links = Object.values(get().assignmentsByPlan[planId] ?? {}).filter(
      (a) => a.partyId === partyId && memberIds.has(a.featureId)
    );
    await Promise.all(
      links.map((a) =>
        get().setOwnerAssignment(planId, a.featureId, partyId, {
          status,
          ...(a.description ? { description: a.description } : {}),
        })
      )
    );
  },

  createOwnerParty: async (planId: string, party: PartyInput, email: string | undefined) => {
    const user = useAuthStore.getState().user;
    if (!user) {
      set({ saveError: 'Sign in to add a responsible party.' });
      return null;
    }
    // Same shape check as firestore.rules (partyContacts), made here so a
    // bad address is a message under the form rather than a denied write.
    if (email && !PARTY_EMAIL_PATTERN.test(email)) {
      set({ saveError: "That email address doesn't look right." });
      return null;
    }
    // Rules cap both names at 80; a longer one (a pasted title, say) would
    // be denied outright.
    party = {
      ...party,
      organization: party.organization?.slice(0, PARTY_NAME_MAX),
      person: party.person?.slice(0, PARTY_NAME_MAX),
    };
    const partyId = defensePlanPartiesApi.newPartyId(planId);
    const now = Timestamp.now();
    const optimistic: PlanParty = {
      id: partyId,
      type: party.type,
      organization: party.organization,
      person: party.person,
      uid: party.uid,
      createdBy: user.id,
      createdAt: now,
      updatedAt: now,
    };

    set((s) => ({
      partiesByPlan: {
        ...s.partiesByPlan,
        [planId]: { ...s.partiesByPlan[planId], [partyId]: optimistic },
      },
      isSaving: true,
      saveError: null,
    }));

    try {
      await enqueueWrite(async () => {
        await defensePlanPartiesApi.createParty(planId, partyId, party, user.id);
        if (email) await defensePlanPartiesApi.setPartyContact(planId, partyId, email);
      });
      set({ isSaving: false });
      return partyId;
    } catch (err) {
      log.error('createOwnerParty failed:', err);
      set((s) => {
        const plan = { ...s.partiesByPlan[planId] };
        delete plan[partyId];
        return {
          partiesByPlan: { ...s.partiesByPlan, [planId]: plan },
          isSaving: false,
          saveError: getErrorMessage(err),
        };
      });
      return null;
    }
  },

  setPartyContactEmail: async (planId, partyId, email) => {
    const trimmed = email?.trim() || undefined;
    if (trimmed && !PARTY_EMAIL_PATTERN.test(trimmed)) {
      set({ saveError: "That email address doesn't look right." });
      return false;
    }
    set({ isSaving: true, saveError: null });
    try {
      await enqueueWrite(() => defensePlanPartiesApi.setPartyContact(planId, partyId, trimmed));
      set({ isSaving: false });
      return true;
    } catch (err) {
      log.error('setPartyContactEmail failed:', err);
      set({ isSaving: false, saveError: getErrorMessage(err) });
      return false;
    }
  },

  updateOwnerParty: async (
    planId: string,
    partyId: string,
    party: PartyInput,
    email: string | undefined
  ) => {
    const existing = get().partiesByPlan[planId]?.[partyId] ?? null;
    if (!existing) return;
    const updated: PlanParty = {
      ...existing,
      type: party.type,
      organization: party.organization,
      person: party.person,
      updatedAt: Timestamp.now(),
    };

    set((s) => ({
      partiesByPlan: {
        ...s.partiesByPlan,
        [planId]: { ...s.partiesByPlan[planId], [partyId]: updated },
      },
      isSaving: true,
      saveError: null,
    }));

    await enqueueWrite(async () => {
      try {
        await defensePlanPartiesApi.updateParty(planId, partyId, party);
        await defensePlanPartiesApi.setPartyContact(planId, partyId, email);
        set({ isSaving: false });
      } catch (err) {
        log.error('updateOwnerParty failed:', err);
        set((s) => ({
          partiesByPlan: {
            ...s.partiesByPlan,
            [planId]: { ...s.partiesByPlan[planId], [partyId]: existing },
          },
          isSaving: false,
          saveError: getErrorMessage(err),
        }));
      }
    });
  },

  deleteOwnerParty: async (planId: string, partyId: string) => {
    const existingParty = get().partiesByPlan[planId]?.[partyId] ?? null;
    if (!existingParty) return;
    const existingAssignments = get().assignmentsByPlan[planId] ?? {};

    set((s) => {
      const parties = { ...s.partiesByPlan[planId] };
      delete parties[partyId];
      const assignments = { ...s.assignmentsByPlan[planId] };
      for (const [id, a] of Object.entries(assignments)) {
        if (a.partyId === partyId) delete assignments[id];
      }
      return {
        partiesByPlan: { ...s.partiesByPlan, [planId]: parties },
        assignmentsByPlan: { ...s.assignmentsByPlan, [planId]: assignments },
        isSaving: true,
        saveError: null,
      };
    });

    let deleted = false;
    await enqueueWrite(async () => {
      try {
        await defensePlanPartiesApi.deleteParty(planId, partyId);
        set({ isSaving: false });
        deleted = true;
      } catch (err) {
        log.error('deleteOwnerParty failed:', err);
        set((s) => ({
          partiesByPlan: {
            ...s.partiesByPlan,
            [planId]: { ...s.partiesByPlan[planId], [partyId]: existingParty },
          },
          assignmentsByPlan: { ...s.assignmentsByPlan, [planId]: existingAssignments },
          isSaving: false,
          saveError: getErrorMessage(err),
        }));
      }
    });
    if (!deleted) return;
    // A deleted party must also leave every bundle team (and stop being a
    // leader), or the bundle row would read "Unknown party". Only the plan's
    // own bundles are touched, and only when something changed.
    const plan = get().ownPlans.find((p) => p.id === planId);
    if (!plan) return;
    const bundles = stripPartyFromBundles(plan.bundles, partyId);
    if (bundles.every((b, i) => b === plan.bundles[i])) return;
    await writePlanUpdate(set, planId, plan, { ...plan, bundles }, 'deleteOwnerParty');
  },

  getPartyContactEmail: async (planId: string, partyId: string) => {
    const contact = await defensePlanPartiesApi.getPartyContact(planId, partyId);
    return contact?.email;
  },

  setOwnerAssignment: async (
    planId: string,
    featureId: string,
    partyId: string,
    data: { description?: string; status: PlanAssignmentStatus }
  ) => {
    const user = useAuthStore.getState().user;
    if (!user) {
      set({ saveError: 'Sign in to assign a defense.' });
      return;
    }
    const id = buildAssignmentId(featureId, partyId);
    const existing = get().assignmentsByPlan[planId]?.[id] ?? null;
    const now = Timestamp.now();
    const optimistic: DefensePlanAssignment = {
      id,
      featureId,
      partyId,
      description: data.description,
      status: data.status,
      source: existing?.source ?? 'owner',
      createdBy: existing?.createdBy ?? user.id,
      createdAt: existing?.createdAt ?? now,
      updatedAt: now,
    };

    set((s) => ({
      assignmentsByPlan: {
        ...s.assignmentsByPlan,
        [planId]: { ...s.assignmentsByPlan[planId], [id]: optimistic },
      },
      isSaving: true,
      saveError: null,
    }));

    await enqueueWrite(async () => {
      try {
        await defensePlanPartiesApi.setOwnerAssignment(planId, featureId, partyId, data, user.id);
        set({ isSaving: false });
      } catch (err) {
        log.error('setOwnerAssignment failed:', err);
        set((s) => {
          const plan = { ...s.assignmentsByPlan[planId] };
          if (existing) plan[id] = existing;
          else delete plan[id];
          return {
            assignmentsByPlan: { ...s.assignmentsByPlan, [planId]: plan },
            isSaving: false,
            saveError: getErrorMessage(err),
          };
        });
      }
    });
  },

  removeAssignment: async (planId: string, assignmentId: string) => {
    const existing = get().assignmentsByPlan[planId]?.[assignmentId] ?? null;
    if (!existing) return;

    set((s) => {
      const plan = { ...s.assignmentsByPlan[planId] };
      delete plan[assignmentId];
      return {
        assignmentsByPlan: { ...s.assignmentsByPlan, [planId]: plan },
        isSaving: true,
        saveError: null,
      };
    });

    await enqueueWrite(async () => {
      try {
        await defensePlanPartiesApi.deleteAssignment(planId, assignmentId);
        set({ isSaving: false });
      } catch (err) {
        log.error('removeAssignment failed:', err);
        set((s) => ({
          assignmentsByPlan: {
            ...s.assignmentsByPlan,
            [planId]: { ...s.assignmentsByPlan[planId], [assignmentId]: existing },
          },
          isSaving: false,
          saveError: getErrorMessage(err),
        }));
      }
    });
  },

  volunteerForFeature: async (planId: string, featureId: string) => {
    const user = useAuthStore.getState().user;
    if (!user) {
      set({ saveError: 'Sign in to volunteer for a defense.' });
      return;
    }

    // Reuse an existing homeowner party this user already has on this plan
    // (from an earlier volunteer on a different defense) rather than
    // minting a duplicate one every time.
    const parties = get().partiesByPlan[planId] ?? {};
    let partyId = Object.values(parties).find((p) => p.uid === user.id)?.id ?? null;
    const isNewParty = !partyId;

    if (partyId) {
      const id = buildAssignmentId(featureId, partyId);
      if (get().assignmentsByPlan[planId]?.[id]) return; // already on this defense
    }

    const now = Timestamp.now();
    if (!partyId) {
      partyId = defensePlanPartiesApi.newPartyId(planId);
      const optimisticParty: PlanParty = {
        id: partyId,
        type: 'homeowner',
        person: volunteerNameFor(user),
        uid: user.id,
        createdBy: user.id,
        createdAt: now,
        updatedAt: now,
      };
      set((s) => ({
        partiesByPlan: {
          ...s.partiesByPlan,
          [planId]: { ...s.partiesByPlan[planId], [partyId as string]: optimisticParty },
        },
      }));
    }

    const id = buildAssignmentId(featureId, partyId);
    const optimisticAssignment: DefensePlanAssignment = {
      id,
      featureId,
      partyId,
      status: 'accepted',
      source: 'volunteer',
      createdBy: user.id,
      createdAt: now,
      updatedAt: now,
    };

    set((s) => ({
      assignmentsByPlan: {
        ...s.assignmentsByPlan,
        [planId]: { ...s.assignmentsByPlan[planId], [id]: optimisticAssignment },
      },
      isSaving: true,
      saveError: null,
    }));

    await enqueueWrite(async () => {
      try {
        if (isNewParty) {
          await defensePlanPartiesApi.createParty(
            planId,
            partyId as string,
            { type: 'homeowner', person: volunteerNameFor(user), uid: user.id },
            user.id
          );
        }
        await defensePlanPartiesApi.createVolunteerAssignment(
          planId,
          featureId,
          partyId as string,
          user.id
        );
        set({ isSaving: false });
      } catch (err) {
        log.error('volunteerForFeature failed:', err);
        set((s) => {
          const assignments = { ...s.assignmentsByPlan[planId] };
          delete assignments[id];
          const partiesForPlan = { ...s.partiesByPlan[planId] };
          if (isNewParty) delete partiesForPlan[partyId as string];
          return {
            assignmentsByPlan: { ...s.assignmentsByPlan, [planId]: assignments },
            partiesByPlan: { ...s.partiesByPlan, [planId]: partiesForPlan },
            isSaving: false,
            saveError: getErrorMessage(err),
          };
        });
      }
    });
  },

  updateAssignmentStatus: async (
    planId: string,
    assignmentId: string,
    status: PlanAssignmentStatus
  ) => {
    const existing = get().assignmentsByPlan[planId]?.[assignmentId] ?? null;
    if (!existing) return;
    const previous = existing;
    const updated: DefensePlanAssignment = { ...existing, status, updatedAt: Timestamp.now() };

    set((s) => ({
      assignmentsByPlan: {
        ...s.assignmentsByPlan,
        [planId]: { ...s.assignmentsByPlan[planId], [assignmentId]: updated },
      },
      isSaving: true,
      saveError: null,
    }));

    await enqueueWrite(async () => {
      try {
        await defensePlanPartiesApi.updateAssignmentStatus(planId, assignmentId, status);
        set({ isSaving: false });
      } catch (err) {
        log.error('updateAssignmentStatus failed:', err);
        set((s) => ({
          assignmentsByPlan: {
            ...s.assignmentsByPlan,
            [planId]: { ...s.assignmentsByPlan[planId], [assignmentId]: previous },
          },
          isSaving: false,
          saveError: getErrorMessage(err),
        }));
      }
    });
  },

  requestPlanUpdate: (planId: string, partyId: string) => requestPlanUpdateApi(planId, partyId),

  publishPlanUpdateNews: (planId: string, updateIds: string[], title?: string) =>
    publishPlanUpdateNewsApi(planId, updateIds, title),

  sendCommunityUpdate: (planId: string, postId: string, includeMembers?: boolean) =>
    sendCommunityUpdateApi(planId, postId, includeMembers),

  clearSaveError: () => set({ saveError: null }),
  clearViewedPlanNotice: () => set({ viewedPlanNotice: null }),

  reset: () => {
    ownPlanUnsub?.();
    ownPlanUnsub = null;
    ownPlanKey = null;
    areaPlansUnsub?.();
    areaPlansUnsub = null;
    areaPlansKey = null;
    viewedPlanUnsub?.();
    viewedPlanUnsub = null;
    viewedPlanKey = null;
    ownPartiesUnsub?.();
    ownPartiesUnsub = null;
    ownPartiesKey = null;
    viewedPartiesUnsub?.();
    viewedPartiesUnsub = null;
    viewedPartiesKey = null;
    ownAssignmentsUnsub?.();
    ownAssignmentsUnsub = null;
    ownAssignmentsKey = null;
    viewedAssignmentsUnsub?.();
    viewedAssignmentsUnsub = null;
    viewedAssignmentsKey = null;
    ownAssignmentUpdatesUnsub?.();
    ownAssignmentUpdatesUnsub = null;
    ownAssignmentUpdatesKey = null;
    ownBidsUnsub?.();
    ownBidsUnsub = null;
    ownBidsKey = null;
    writeChain = Promise.resolve();
    latestPlanWriteSeq.clear();
    lastPlanWriteStartedAt.clear();
    set({
      ownPlans: [],
      activeOwnPlanId: null,
      draftingNewPlan: false,
      pendingTitle: null,
      ownPlansLoaded: false,
      ownPlan: null,
      areaPlans: [],
      viewedPlan: null,
      isSaving: false,
      saveError: null,
      viewedPlanNotice: null,
      partiesByPlan: {},
      assignmentsByPlan: {},
      assignmentUpdatesByPlan: {},
      bidsByPlan: {},
      fundingRequestsByPlan: {},
      unpersistedPlanIds: {},
      hiddenPartyIds: {},
    });
  },
}));

/**
 * Bundles to keep through pruneEmptyBundles even with no members: those with
 * an upcoming event, those open for bids or with a vendor's bid on them, and
 * those with a live funding request.
 */
function keptBundleIds(planId: string, bundles: PlanBundle[]): Set<string> {
  const keep = bundleIdsKeptByEvents(planId);
  const bids = useDefensePlansStore.getState().bidsByPlan[planId] ?? [];
  for (const id of bundleIdsKeptByBids(bundles, bids)) keep.add(id);
  const requests = useDefensePlansStore.getState().fundingRequestsByPlan[planId] ?? [];
  for (const id of bundleIdsKeptByFunding(requests)) keep.add(id);
  return keep;
}

/** Owner's accept or decline, shown at once and put back if the write fails. */
async function writeBidStatus(
  set: SetPlanState,
  planId: string,
  bidId: string,
  status: 'accepted' | 'declined',
  acceptedPartyId?: string
): Promise<boolean> {
  const patch = (next: Partial<PlanBid>) =>
    set((s) => ({
      bidsByPlan: {
        ...s.bidsByPlan,
        [planId]: (s.bidsByPlan[planId] ?? []).map((b) => (b.id === bidId ? { ...b, ...next } : b)),
      },
    }));
  patch({ status, ...(acceptedPartyId ? { acceptedPartyId } : {}) });
  set({ isSaving: true, saveError: null });
  try {
    await planBidsApi.setBidStatus(planId, bidId, status, acceptedPartyId);
    set({ isSaving: false });
    return true;
  } catch (err) {
    log.error(`writeBidStatus(${status}) failed:`, err);
    patch({ status: 'submitted', acceptedPartyId: undefined });
    set({ isSaving: false, saveError: getErrorMessage(err) });
    return false;
  }
}

/** "Bid of $12,000" for the vendor's line on the team, or nothing without a price. */
function bidTeamDescription(bid: PlanBid): string | undefined {
  if (bid.amount == null) return undefined;
  const price = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(bid.amount);
  return `Bid of ${price}`;
}
